import {
  siteBuilderBlockDelegatesOutlineToColumn,
  type SiteBuilderBlock,
  type SiteBuilderZoneKind,
} from "@webdo/site-templates/builder";

const builderBlockSelector = "[data-builder-block-path]";
const builderColumnSelector = "[data-builder-column-root]";
const builderInlineTextSelector = "[data-builder-inline-text-part]";

export function builderInlineEditingTextWithoutPlaceholders(
  value: string,
): string {
  return value.replaceAll("\u200b", "");
}

export function builderColumnWithFirstInlineBlock(
  column: SiteBuilderBlock,
  inlineBlock: SiteBuilderBlock,
): SiteBuilderBlock | null {
  if (
    column.type !== "column"
    || (column.slots?.content?.length ?? 0) > 0
    || (column.children?.length ?? 0) > 0
  ) {
    return null;
  }

  return {
    ...column,
    slots: {
      ...column.slots,
      content: [inlineBlock],
    },
  };
}

export function builderTextColumnContent(
  block: SiteBuilderBlock,
): SiteBuilderBlock | null {
  const content = block.slots?.content;

  return (
    block.type === "column"
    && block.options?.builderTextColumn === true
    && content?.length === 1
  ) ? content[0] ?? null : null;
}

/**
 * Text blocks use a movable Column on a spatial canvas, but structured flows
 * consume the authored inner block. A restricted slot such as `form.items`
 * may also consume that inner block when it deliberately rejects Columns.
 */
export function builderTextColumnBlockForTarget(
  block: SiteBuilderBlock,
  {
    inlineAccepted,
    rawAccepted,
    wrapBlock,
    zoneKind,
  }: {
    inlineAccepted: boolean;
    rawAccepted: boolean;
    wrapBlock?: (block: SiteBuilderBlock) => SiteBuilderBlock;
    zoneKind: SiteBuilderZoneKind | null;
  },
): SiteBuilderBlock {
  const inlineBlock = builderTextColumnContent(block);

  if (
    inlineBlock
    && inlineAccepted
    && (!rawAccepted || zoneKind === "textFlow")
  ) {
    return inlineBlock;
  }

  return !inlineBlock && zoneKind === "grid" && wrapBlock
    ? wrapBlock(block)
    : block;
}

/**
 * Placement probes the inner type first so a selected restricted slot can be
 * discovered before a broader ancestor grid. Final materialization still
 * keeps the Column whenever that spatial target accepts it.
 */
export function builderTextColumnPlacementCandidateTypes(
  block: SiteBuilderBlock,
): string[] {
  const inlineType = builderTextColumnContent(block)?.type;

  return inlineType && inlineType !== block.type
    ? [inlineType, block.type]
    : [block.type];
}

/**
 * Extends the inline-edit hit area to the visual row of a plain textual child.
 * Empty space in an ordinary Column stays owned by the Column. The editor
 * handles the dedicated one-child `builderTextColumn` case separately so a
 * mobile tap can still reach its intentionally wrapped text.
 */
export function builderColumnInlineEditTarget(
  target: Element,
  _clientY: number,
): HTMLElement | null {
  const column = target.closest(builderColumnSelector);

  if (!column) {
    return null;
  }

  const clickedBlock = target.closest(builderBlockSelector);
  const clickedType = clickedBlock?.getAttribute("data-builder-block-type");

  if (
    !clickedBlock
    || clickedBlock === column
    || !siteBuilderBlockDelegatesOutlineToColumn(clickedType)
  ) {
    return null;
  }

  for (const candidate of clickedBlock.querySelectorAll<HTMLElement>(
    builderInlineTextSelector,
  )) {
    const block = candidate.closest(builderBlockSelector);

    if (
      block !== clickedBlock
      || block.closest(builderColumnSelector) !== column
    ) {
      continue;
    }

    return candidate;
  }

  return null;
}

/**
 * Resolves a click on the Column surface itself. The document layer uses this
 * to materialize the first empty Text block before starting inline editing.
 * Nested blocks keep ownership of their own click area.
 */
export function builderDirectColumnEditTarget(
  target: Element,
): HTMLElement | null {
  const column = target.closest(builderColumnSelector);

  if (!column) {
    return null;
  }

  const clickedBlock = target.closest(builderBlockSelector);

  return !clickedBlock || clickedBlock === column
    ? column as HTMLElement
    : null;
}
